/** Pure motion skeleton preview logic — projection, schema, temporal analysis. */

import type { MotionPreviewFrame, MotionPreviewPayload } from './motionAssetPreview'

export const SKELETON_SCHEMA_H36M_17 = 'h36m_17'

/** Matches backend `motion_asset_schema.H36M_JOINT_NAMES`. */
export const H36M_JOINT_NAMES = [
  'Hip',
  'RHip',
  'RKnee',
  'RAnkle',
  'LHip',
  'LKnee',
  'LAnkle',
  'Spine',
  'Thorax',
  'Neck',
  'Head',
  'LShoulder',
  'LElbow',
  'LWrist',
  'RShoulder',
  'RElbow',
  'RWrist',
] as const

/** Matches backend `motion_asset_schema.H36M_BONES`. */
export const H36M_BONES: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [1, 2],
  [2, 3],
  [0, 4],
  [4, 5],
  [5, 6],
  [0, 7],
  [7, 8],
  [8, 9],
  [9, 10],
  [8, 11],
  [11, 12],
  [12, 13],
  [8, 14],
  [14, 15],
  [15, 16],
]

export type MotionCoordinateSpace = 'camera_normalized' | 'root_relative_y_up_meters' | 'unknown'

export type ProjectedJoint = { x: number; y: number; c: number }

export type StableProjectionBounds = {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

const CONFIDENCE_MIN = 0.15
const BOUNDS_PADDING = 0.08

export function resolveCoordinateSpace(payload: Pick<MotionPreviewPayload, 'coordinate_space'> | null | undefined): MotionCoordinateSpace {
  const raw = payload?.coordinate_space
  if (raw === 'camera_normalized') return 'camera_normalized'
  if (raw === 'root_relative_y_up_meters') return 'root_relative_y_up_meters'
  return 'unknown'
}

function jointXY(joint: { x: number; y: number; z?: number }): { x: number; y: number } {
  return { x: Number(joint.x), y: Number(joint.y) }
}

/** Infer coordinate space when API omits `coordinate_space`. */
export function inferCoordinateSpace(frames: MotionPreviewFrame[]): MotionCoordinateSpace {
  let min = Infinity
  let max = -Infinity
  for (const frame of frames) {
    for (const joint of frame.joints || []) {
      min = Math.min(min, Number(joint.x), Number(joint.y))
      max = Math.max(max, Number(joint.x), Number(joint.y))
    }
  }
  if (!Number.isFinite(min)) return 'unknown'
  if (min >= -0.05 && max <= 1.05) return 'camera_normalized'
  if (min >= -2 && max <= 2) return 'root_relative_y_up_meters'
  return 'unknown'
}

/** Compute sequence-stable bounds for root-relative / unknown spaces. */
export function computeStableProjectionBounds(
  frames: MotionPreviewFrame[],
  coordinateSpace: MotionCoordinateSpace,
): StableProjectionBounds | null {
  if (coordinateSpace === 'camera_normalized') return null
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const frame of frames) {
    for (const joint of frame.joints || []) {
      if (Number(joint.confidence ?? 1) < CONFIDENCE_MIN) continue
      const { x, y } = jointXY(joint)
      minX = Math.min(minX, x)
      maxX = Math.max(maxX, x)
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)
    }
  }
  if (!Number.isFinite(minX)) return null
  const spanX = Math.max(maxX - minX, 1e-4)
  const spanY = Math.max(maxY - minY, 1e-4)
  return {
    minX: minX - spanX * BOUNDS_PADDING,
    maxX: maxX + spanX * BOUNDS_PADDING,
    minY: minY - spanY * BOUNDS_PADDING,
    maxY: maxY + spanY * BOUNDS_PADDING,
  }
}

export function projectFrameJoints(input: {
  frame: MotionPreviewFrame | undefined
  width: number
  height: number
  coordinateSpace: MotionCoordinateSpace
  bounds: StableProjectionBounds | null
}): ProjectedJoint[] {
  const { frame, width, height, coordinateSpace, bounds } = input
  if (!frame?.joints?.length) return []

  return frame.joints.map((joint) => {
    const c = Number(joint.confidence ?? 1)
    const { x, y } = jointXY(joint)
    if (coordinateSpace === 'camera_normalized') {
      return { x: x * width, y: (1 - y) * height, c }
    }
    if (bounds) {
      const nx = (x - bounds.minX) / Math.max(bounds.maxX - bounds.minX, 1e-6)
      const ny = (y - bounds.minY) / Math.max(bounds.maxY - bounds.minY, 1e-6)
      return { x: nx * width, y: (1 - ny) * height, c }
    }
    // Legacy centered fallback for small root-relative values.
    return { x: (x + 0.5) * width, y: (1 - (y + 0.5)) * height, c }
  })
}

export function previewDurationSeconds(payload: Pick<MotionPreviewPayload, 'frames' | 'preview_fps' | 'frame_count'>): number {
  const fps = payload.preview_fps || 12
  const count = payload.frame_count ?? payload.frames?.length ?? 0
  return count > 0 ? count / fps : 0
}

export function analyzeTemporalMotion(frames: MotionPreviewFrame[], jointIndices: number[]): {
  present: boolean
  maxDelta: number
} {
  if (frames.length <= 1) return { present: false, maxDelta: 0 }
  const mid = Math.floor(frames.length / 2)
  const last = frames.length - 1
  let maxDelta = 0
  for (const jointIndex of jointIndices) {
    const a = frames[0]?.joints?.[jointIndex]
    const b = frames[mid]?.joints?.[jointIndex]
    const c = frames[last]?.joints?.[jointIndex]
    if (!a || !b || !c) continue
    maxDelta = Math.max(
      maxDelta,
      Math.hypot(Number(b.x) - Number(a.x), Number(b.y) - Number(a.y)),
      Math.hypot(Number(c.x) - Number(b.x), Number(c.y) - Number(b.y)),
    )
  }
  return { present: maxDelta > 1e-4, maxDelta }
}

/** Advance playback frame index (looping). */
export function advancePlaybackFrameIndex(current: number, frameCount: number, loop: boolean): number | null {
  if (frameCount <= 1) return null
  const next = current + 1
  if (next >= frameCount) return loop ? 0 : null
  return next
}

export function schemaMatchesBackend(): boolean {
  return H36M_BONES.length === 16 && H36M_JOINT_NAMES.length === 17
}

export function boneConnectsExpectedJoints(bone: readonly [number, number], jointNames: readonly string[]): boolean {
  const [a, b] = bone
  return a >= 0 && b >= 0 && a < jointNames.length && b < jointNames.length
}
